import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100";

const Field = ({ label, k, area, form, set }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    {area ? (
      <textarea rows={3} className={inputCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`content-${k}`} />
    ) : (
      <input className={inputCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`content-${k}`} />
    )}
  </div>
);

export default function ContentManage() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newT, setNewT] = useState({ quote_text: "", author_name: "", date: "" });

  const { data: content } = useQuery({ queryKey: ["site-content"], queryFn: async () => (await api.get("/site-content")).data });
  const { data: testimonials = [] } = useQuery({ queryKey: ["admin-testimonials"], queryFn: async () => (await api.get("/admin/testimonials")).data });

  useEffect(() => { if (content && !form) setForm(content); }, [content, form]);

  if (!form) return <p className="text-slate-500">Loading…</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...(f.social_links || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", form);
      qc.invalidateQueries({ queryKey: ["site-content"] });
      toast.success("Content saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addTestimonial = async () => {
    if (!newT.quote_text || !newT.author_name) { toast.error("Quote and author are required"); return; }
    await api.post("/admin/testimonials", newT);
    setNewT({ quote_text: "", author_name: "", date: "" });
    qc.invalidateQueries({ queryKey: ["admin-testimonials"] });
    qc.invalidateQueries({ queryKey: ["testimonials"] });
    toast.success("Testimonial added");
  };

  const removeTestimonial = async (id) => {
    await api.delete(`/admin/testimonials/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-testimonials"] });
    qc.invalidateQueries({ queryKey: ["testimonials"] });
    toast.success("Removed");
  };



  return (
    <div data-testid="content-manage-page">
      <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">Site content</h1>
      <p className="text-slate-500 mt-1">Edit the text shown across your public website.</p>

      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm space-y-4">
        <h2 className="font-display font-bold text-slate-900">Brand & hero</h2>
        <Field form={form} set={set} label="Company name" k="company_name" />
        <Field form={form} set={set} label="Tagline" k="tagline" />
        <Field form={form} set={set} label="Strapline" k="strapline" />
        <Field form={form} set={set} label="Hero text" k="hero_text" area />
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm space-y-4">
        <h2 className="font-display font-bold text-slate-900">About & contact</h2>
        <Field form={form} set={set} label="About text" k="about_text" area />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field form={form} set={set} label="Phone" k="contact_phone" />
          <Field form={form} set={set} label="Email" k="contact_email" />
        </div>
        <Field form={form} set={set} label="Address" k="contact_address" />
        <Field form={form} set={set} label="Service area" k="service_area" />
        <Field form={form} set={set} label="Map location (town or postcode shown on the About page map)" k="map_query" />
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Facebook URL</label><input className={inputCls} value={form.social_links?.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} data-testid="content-facebook" /></div>
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Instagram URL</label><input className={inputCls} value={form.social_links?.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} data-testid="content-instagram" /></div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm space-y-4">
        <h2 className="font-display font-bold text-slate-900">Legal details</h2>
        <p className="text-sm text-slate-500">Shown in the footer and legal pages. Leave blank until you have them.</p>
        <Field form={form} set={set} label="Company number" k="company_number" />
        <Field form={form} set={set} label="Registered office address" k="registered_office" />
      </div>

      <button onClick={save} disabled={saving} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-60" data-testid="content-save">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save content
      </button>

      {/* Testimonials */}
      <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <h2 className="font-display font-bold text-slate-900">Testimonials</h2>
        <div className="mt-4 space-y-3">
          {testimonials.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 p-4" data-testid={`testimonial-${t.id}`}>
              <div><p className="text-slate-700 text-sm">"{t.quote_text}"</p><p className="text-xs font-semibold text-slate-500 mt-1">{t.author_name} · {t.date}</p></div>
              <button onClick={() => removeTestimonial(t.id)} data-testid={`testimonial-delete-${t.id}`} className="shrink-0 h-9 w-9 rounded-lg bg-red-50 grid place-items-center hover:bg-red-100"><Trash2 className="h-4 w-4 text-red-600" /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-slate-100 pt-4 space-y-3">
          <textarea rows={2} className={inputCls} placeholder="Quote text" value={newT.quote_text} onChange={(e) => setNewT({ ...newT, quote_text: e.target.value })} data-testid="testimonial-new-quote" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Author (e.g. Karen H., Sale)" value={newT.author_name} onChange={(e) => setNewT({ ...newT, author_name: e.target.value })} data-testid="testimonial-new-author" />
            <input className={inputCls} placeholder="Date (e.g. 2026-05)" value={newT.date} onChange={(e) => setNewT({ ...newT, date: e.target.value })} data-testid="testimonial-new-date" />
          </div>
          <button onClick={addTestimonial} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700" data-testid="testimonial-add"><Plus className="h-4 w-4" /> Add testimonial</button>
        </div>
      </div>
    </div>
  );
}
